import { lazy } from 'react';
import { DollarSign, Workflow, Users, Shield, Database, RefreshCw, Activity, TrendingUp, BarChart3, MessageSquare, Trello, Target, UserCircle, Building2, Briefcase, Newspaper, Sparkles, Wrench, BookOpen, LineChart, Palette, type LucideIcon } from 'lucide-react';
import React from 'react';

import { useAuthStore } from '@/shared/stores/authStore';
import { hasRouteAccess as hasGroupRouteAccess, hasCapability } from '@/lib/group-access';
import type { Capability } from '@monthly-data-collection/shared';

// Domain route interface
export interface DomainRoute {
  path: string;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  name: string;
  icon?: LucideIcon;
  description?: string;
  /**
   * Capability required on top of the route grant. Enforced by
   * hasAccessToRoute, which nav, search, and the App.tsx redirect all flow
   * through — a route grant alone is not enough to reach these pages.
   */
  requiredCapability?: Capability;
}

// Domain configuration interface
export interface DomainConfig {
  id: string;
  name: string;
  displayName: string;
  icon: LucideIcon;
  defaultRoute: string;
  routes: DomainRoute[];
  description: string;
  hidden?: boolean; // If true, excluded from the homepage domain sidebar
  hiddenFromSwitcher?: boolean; // If true, excluded from the sidebar domain switcher
}

// Lazy-loaded billing components
const BillingDashboard = lazy(() => import('./domains/billing/pages/Dashboard'));
const BillingSetup = lazy(() => import('./domains/billing/pages/Setup'));
const BillingMyTasks = lazy(() => import('./domains/billing/pages/MyTasks'));
const BillingTeamTasks = lazy(() => import('./domains/billing/pages/TeamTasks'));
const BillingReview = lazy(() => import('./domains/billing/pages/Review'));
const BillingArchive = lazy(() => import('./domains/billing/pages/Archive'));

// Lazy-loaded admin components
const AdminUsers = lazy(() => import('./domains/admin/pages/Users'));
const AdminDataImport = lazy(() => import('./domains/admin/pages/DataImport'));
const AdminCycles = lazy(() => import('./domains/admin/pages/Cycles'));
const AdminSpendConfig = lazy(() => import('./domains/admin/pages/SpendConfig'));
const AdminSlackIntegration = lazy(() => import('./domains/admin/pages/SlackIntegration'));
const AdminSlackTemplates = lazy(() => import('./domains/admin/pages/SlackTemplates'));
const AdminAsanaIntegration = lazy(() => import('./domains/admin/pages/AsanaIntegration'));
const AdminApolloEnrichment = lazy(() => import('./domains/admin/pages/ApolloEnrichment'));
const AdminFunctionHealth = lazy(() => import('./domains/admin/pages/FunctionHealth'));

// Lazy-loaded workflow components
const WorkflowTeamPlanning = lazy(() => import('./domains/workflow/pages/TeamPlanning'));
const WorkflowTeamWorkflow = lazy(() => import('./domains/workflow/pages/TeamWorkflow'));
const WorkflowSummary = lazy(() => import('./domains/workflow/pages/WorkflowSummary'));
const WorkflowCapacity = lazy(() => import('./domains/workflow/pages/Capacity'));
const WorkflowHiringCapacity = lazy(() => import('./domains/workflow/pages/HiringCapacity'));
const WorkflowAdmin = lazy(() => import('./domains/workflow/pages/WorkflowAdmin'));

// Lazy-loaded hiring components
const HiringForecast = lazy(() => import('./domains/hiring/pages/Forecast'));
const HiringHeadcount = lazy(() => import('./domains/hiring/pages/Headcount'));
const HiringTiming = lazy(() => import('./domains/hiring/pages/Timing'));
const HiringPlacement = lazy(() => import('./domains/hiring/pages/Placement'));
const HiringCalendar = lazy(() => import('./domains/hiring/pages/Calendar'));
const HiringRateCard = lazy(() => import('./domains/hiring/pages/RateCard'));
const HiringPositionLedger = lazy(() => import('./domains/hiring/pages/PositionLedger'));
const HiringEnvelopes = lazy(() => import('./domains/hiring/pages/Envelopes'));
const HiringRollup = lazy(() => import('./domains/hiring/pages/Rollup'));
const HiringHealth = lazy(() => import('./domains/hiring/pages/Health'));

// Lazy-loaded sales components
const SalesWorkflow = lazy(() => import('./domains/sales/pages/Workflow'));
const SalesAudits = lazy(() => import('./domains/sales/pages/Audits'));
const SalesPipeline = lazy(() => import('./domains/sales/pages/Pipeline'));
const SalesDeals = lazy(() => import('./domains/sales/pages/Deals'));
const SalesClosed = lazy(() => import('./domains/sales/pages/Closed'));
const SalesFirmographics = lazy(() => import('./domains/sales/pages/Firmographics'));
const SalesResourcing = lazy(() => import('./domains/sales/pages/Resourcing'));
const SalesPricing = lazy(() => import('./domains/sales/pages/Pricing'));

// Lazy-loaded intel components
const IntelOverview = lazy(() => import('./domains/intel/pages/Overview'));
const IntelProjects = lazy(() => import('./domains/intel/pages/Projects'));
const IntelTeamMembers = lazy(() => import('./domains/intel/pages/TeamMembers'));
const IntelCompany = lazy(() => import('./domains/intel/pages/Company'));
const IntelCreative = lazy(() => import('./domains/intel/pages/Creative'));
const IntelDataReporting = lazy(() => import('./domains/intel/pages/DataReporting'));
const IntelFteReporting = lazy(() => import('./domains/intel/pages/FteReporting'));

// Lazy-loaded goals components
const GoalsGrow = lazy(() => import('./domains/goals/pages/Grow'));
const GoalsRun = lazy(() => import('./domains/goals/pages/Run'));
const GoalsTransform = lazy(() => import('./domains/goals/pages/Transform'));

// Lazy-loaded CRM components
const CRMCompanies = lazy(() => import('./domains/crm/pages/Companies'));
const CRMCompanyDetail = lazy(() => import('./domains/crm/pages/CompanyDetail'));
const CRMPeople = lazy(() => import('./domains/crm/pages/People'));
const CRMPersonDetail = lazy(() => import('./domains/crm/pages/PersonDetail'));
const CRMJobPosts = lazy(() => import('./domains/crm/pages/JobPosts'));
const CRMNews = lazy(() => import('./domains/crm/pages/News'));
const CRMRoleChanges = lazy(() => import('./domains/crm/pages/RoleChanges'));
const CRMMoments = lazy(() => import('./domains/crm/pages/Moments'));

// Lazy-loaded tools components
const ToolsOverview = lazy(() => import('./domains/tools/pages/Overview'));
const ToolsCharts = lazy(() => import('./domains/tools/pages/Charts'));
const ToolsCompensationCalculator = lazy(() => import('./domains/tools/pages/CompensationCalculator'));

// Lazy-loaded knowledge components
const KnowledgePage = lazy(() => import('./domains/knowledge/pages/KnowledgePage'));

// Lazy-loaded creative components
const CreativeWorkflow = lazy(() => import('./domains/creative/pages/Workflow'));
const CreativeIntel = lazy(() => import('./domains/creative/pages/Intel'));
const CreativeTeam = lazy(() => import('./domains/creative/pages/Team'));
const CreativeProjects = lazy(() => import('./domains/creative/pages/Projects'));

// Lazy-loaded forecast components
const ForecastPage = lazy(() => import('./domains/forecast/pages/ForecastPage'));

// Domain configurations
export const DOMAINS: Record<string, DomainConfig> = {
  goals: {
    id: 'goals',
    name: 'goals',
    displayName: 'Goals',
    icon: Target,
    defaultRoute: '/goals/critical-success-factors',
    description: 'Track and manage goals across critical success factors (CSFs), company and department initiatives',
    routes: [
      {
        path: '/goals/department-initiatives',
        component: GoalsGrow,
        name: 'Department Initiatives',
        description: 'Browse and manage Asana-synced project portfolios by department. Select a portfolio to view a configurable project table with custom columns, due dates, and owners',
        icon: TrendingUp,
      },
      {
        path: '/goals/critical-success-factors',
        component: GoalsRun,
        name: 'Critical Success Factors',
        description: 'Track operational health via Critical Success Factors (CSFs) organized into company-wide and department cards. Update status colors, configure metric charts with custom formulas, and log quarterly updates',
        icon: Activity,
      },
      {
        path: '/goals/company-initiatives',
        component: GoalsTransform,
        name: 'Company Initiatives',
        description: 'Manage large-scale strategic initiatives with executive sponsors, milestone tracking, RACI assignments, and expected outcomes toward company-level goals',
        icon: Target,
      },
    ],
  },
  crm: {
    id: 'crm',
    name: 'crm',
    displayName: 'CRM',
    icon: UserCircle,
    defaultRoute: '/crm/companies',
    description: 'CRM for client relationships, companies, and point-of-contacts (POCs).',
    routes: [
      {
        path: '/crm/companies',
        component: CRMCompanies,
        name: 'Companies',
        description: 'Filterable table of all client companies with Overview, Firmographics, and Finance view modes. Filter by status, account owner, program, and division',
        icon: Building2,
      },
      {
        path: '/crm/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company Detail',
        icon: Building2,
      },
      // Company detail tab routes - page-level focus comes first
      {
        path: '/crm/activity/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company Activity',
        icon: Activity,
      },
      {
        path: '/crm/reviews/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company Reviews',
        icon: MessageSquare,
      },
      {
        path: '/crm/finances/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company Finances',
        icon: DollarSign,
      },
      {
        path: '/crm/people/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company People',
        icon: Users,
      },
      {
        path: '/crm/legal/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company Legal',
        icon: Shield,
      },
      {
        path: '/crm/job-posts/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company Job Posts',
        icon: Briefcase,
      },
      {
        path: '/crm/news/companies/:orgId',
        component: CRMCompanyDetail,
        name: 'Company News',
        icon: Newspaper,
      },
      {
        path: '/crm/people',
        component: CRMPeople,
        name: 'People',
        description: 'Paginated point-of-contact (POC) directory for all CRM people across client companies. Filter by company, role, country, seniority, and data quality flags',
        icon: Users,
      },
      {
        path: '/crm/people/:personId',
        component: CRMPersonDetail,
        name: 'Person Detail',
        icon: Users,
      },
      {
        path: '/crm/job-posts',
        component: CRMJobPosts,
        name: 'Job Posts',
        description: 'Feed of job postings from client companies surfaced via Apollo enrichment. Filter by account owner, program, and division to spot hiring activity',
        icon: Briefcase,
      },
      {
        path: '/crm/news',
        component: CRMNews,
        name: 'News',
        description: 'Feed of news articles about client companies from Apollo enrichment. Filter by account owner, program, and division to monitor press coverage and signals',
        icon: Newspaper,
      },
      {
        path: '/crm/role-changes',
        component: CRMRoleChanges,
        name: 'Role Changes',
        description: 'Timeline of contact career moves at client companies, auto-filtered to the last 12 months. Tracks job changes, promotions, and departures for relationship follow-up',
        icon: Users,
      },
      {
        path: '/crm/moments/*',
        component: CRMMoments,
        name: 'Key Moments',
        icon: Sparkles,
      },
    ],
  },
  intel: {
    id: 'intel',
    name: 'intel',
    displayName: 'Intel',
    icon: BarChart3,
    defaultRoute: '/intel/overview',
    description: 'Business intelligence (BI) and internal reporting dashboard',
    routes: [
      // Overview routes - accessible to all authenticated users
      {
        path: '/intel/overview/*',
        component: IntelOverview,
        name: 'Overview',
        icon: BarChart3,
      },

      // Project routes - granular page-level permissions
      {
        path: '/intel/projects/overview/*',
        component: IntelProjects,
        name: 'Project Overview',
      },
      {
        path: '/intel/projects/health/*',
        component: IntelProjects,
        name: 'Project Health',
      },
      {
        path: '/intel/projects/performance/*',
        component: IntelProjects,
        name: 'Project Performance',
      },
      {
        path: '/intel/projects/financials/*',
        component: IntelProjects,
        name: 'Project Financials',
      },
      {
        path: '/intel/projects/team/*',
        component: IntelProjects,
        name: 'Project Team',
      },
      {
        path: '/intel/projects/people/*',
        component: IntelProjects,
        name: 'Project People',
      },

      // Team member routes - granular page-level permissions
      {
        path: '/intel/team-members/overview/*',
        component: IntelTeamMembers,
        name: 'Team Member Overview',
      },
      {
        path: '/intel/team-members/performance/*',
        component: IntelTeamMembers,
        name: 'Team Member Performance',
      },
      {
        path: '/intel/team-members/network/*',
        component: IntelTeamMembers,
        name: 'Team Member Network',
      },

      // Catch-all routes (MUST be last to allow specific routes to match first)
      // These handle base paths and any unmatched page paths
      {
        path: '/intel/projects/*',
        component: IntelProjects,
        name: 'Projects',
        description: 'Business intelligence (BI) per project or client with information about spend, revenue, health score, performance, and team information',
        icon: Briefcase,
      },
      {
        path: '/intel/team-members/*',
        component: IntelTeamMembers,
        name: 'Team Members',
        description: 'Business intelligence (BI) per team member with information about channel experience, project leadership, background and network',
        icon: Users,
      },
      {
        path: '/intel/company/creative',
        component: IntelCreative,
        name: 'Creative',
        description: 'Analytics dashboard for Creative workflows. View aggregated performance metrics, 9-month client retention trends, and Creative team portfolio data',
      },
      {
        path: '/intel/company/data',
        component: IntelDataReporting,
        name: 'Data',
        description: 'Analytics dashboard for Data workflows. View aggregated performance metrics and data team portfolio reporting',
      },
      {
        path: '/intel/company/fte-reporting',
        component: IntelFteReporting,
        name: 'FTE Reporting',
        description: 'FTE category heatmap showing how team capacity is allocated across internal and client-facing categories. Filter by department seniority to analyze staffing',
      },
      {
        path: '/intel/company/*',
        component: IntelCompany,
        name: 'Company',
        icon: Building2,
      },
    ],
  },
  creative: {
    id: 'creative',
    name: 'creative',
    displayName: 'Creative',
    icon: Palette,
    defaultRoute: '/creative/intel',
    description: 'Creative department intelligence, workflow management, and team performance',
    routes: [
      {
        path: '/creative/intel',
        component: CreativeIntel,
        name: 'Intel',
        description: 'Performance reporting for the Creative team',
        icon: BarChart3,
      },
      {
        path: '/creative/workflow',
        component: CreativeWorkflow,
        name: 'Workflow',
        description: 'Manage creative workflow assignments, team roles, and project allocation',
        icon: Workflow,
      },
      {
        path: '/creative/team/*',
        component: CreativeTeam,
        name: 'Team',
        description: 'Overview and performance for Creative team members',
        icon: Users,
      },
      {
        path: '/creative/projects/*',
        component: CreativeProjects,
        name: 'Projects',
        description: 'Project-level analytics for Creative workflows',
        icon: Briefcase,
      },
    ],
  },
  workflow: {
    id: 'workflow',
    name: 'workflow',
    displayName: 'Workflow',
    icon: Workflow,
    defaultRoute: '/workflow/summary',
    description: 'Design, manage, and optimize business workflows and processes',
    routes: [
      {
        path: '/workflow/team-planning',
        component: WorkflowTeamPlanning,
        name: 'Team Planning',
        description: 'Embedded Google Sheets workspace for team planning. Select and switch between multiple planning sheets per team, with sheet management built in',
      },
      {
        path: '/workflow/team-workflow/*',
        component: WorkflowTeamWorkflow,
        name: 'Team Workflow',
        description: 'Manage team and program level workflow. Request new project and hiring capacity spots, see global workflow assignments across team members',
        icon: Users,
      },
      {
        path: '/workflow/summary',
        component: WorkflowSummary,
        name: 'Workflow Summary',
        description: 'Executive-level Division of Labour view. Manage divisions, programs, employee assignments, and leadership responsibility with BigQuery revenue data',
      },
      {
        path: '/workflow/capacity/*',
        component: WorkflowCapacity,
        name: 'Capacity',
        description: 'Approve and review project capacity spots',
        icon: Users,
      },
      {
        path: '/workflow/hiring-capacity/*',
        component: WorkflowHiringCapacity,
        name: 'Hiring Capacity',
        description: 'Approve and review hiring capacity spots',
        icon: Users,
      },
      {
        path: '/workflow/admin',
        component: WorkflowAdmin,
        name: 'Admin',
        description: 'Admin panel for workflow data quality and bulk operations. Detect container issues, manage workflow and hiring spots in bulk, and reassign workflows',
        icon: Shield,
      },
    ],
  },
  billing: {
    id: 'billing',
    name: 'billing',
    displayName: 'Billing',
    icon: DollarSign,
    defaultRoute: '/billing/dashboard',
    description: 'Manage billing data, input FTE, performance, business intelligence questions and monthly spend per client or workflow',
    routes: [
      {
        path: '/billing/dashboard',
        component: BillingDashboard,
        name: 'Dashboard',
        description: 'Read-only overview of workflow and user progress across the current billing cycle. Filter by program and status to track setup, data entry, and completion',
        icon: DollarSign,
      },
      {
        path: '/billing/setup/*',
        component: BillingSetup,
        description: 'Setup billing process, manage workflow assignments, assign people to programs and manage deal information',
        name: 'Setup',
      },
      {
        path: '/billing/my-tasks',
        component: BillingMyTasks,
        name: 'My Tasks',
        description: 'Individual data entry for FTE allocation, categories, relationships, and performance. Fill in assigned workflows across four tabs and submit when complete',
      },
      {
        path: '/billing/team-tasks',
        component: BillingTeamTasks,
        name: 'Team Tasks',
        description: 'Collaborative spend data entry. Enter spend amounts by channel and tactic per workflow, scoped to a program',
      },
      {
        path: '/billing/review',
        component: BillingReview,
        name: 'Review',
        description: 'QA and approval of submitted billing data. Review and bulk approve or reject line items, inspect anomalies, and compare month-over-month spend',
      },
      {
        path: '/billing/archive',
        component: BillingArchive,
        name: 'Archive',
        description: 'Read-only access to historical billing cycles. Browse past submissions, view modification history, filter by tags, and export cycle data',
      },
    ],
  },
  hiring: {
    id: 'hiring',
    name: 'hiring',
    displayName: 'Hiring',
    icon: Users,
    defaultRoute: '/hiring/forecast',
    description: 'Manage recruitment, candidate tracking, and hiring processes',
    routes: [
      {
        path: '/hiring/forecast',
        component: HiringForecast,
        name: 'Forecast',
        description: 'Balance top-down revenue targets with bottom-up role-level hiring projections. Planned hires derive from committed Position Planning rows; trigger recalculation and review variance alerts',
        icon: Users,
      },
      {
        path: '/hiring/headcount',
        component: HiringHeadcount,
        name: 'Headcount',
        description: 'Analyze historical hiring performance and validate forecast assumptions. View 24-month headcount trends, rolling averages for hire/turnover/promotion rates',
        icon: Users,
      },
      {
        path: '/hiring/timing/*',
        component: HiringTiming,
        name: 'Timing',
        description: 'Visualize and plan hiring capacity spots by month to balance talent acquisition workflow',
        icon: Users,
      },
      {
        path: '/hiring/placement',
        component: HiringPlacement,
        name: 'Placement',
        description: 'Match confirmed hires to open hiring spots from Workflow. Manage hire records grouped by month, upload resumes and scorecards, and link hires with spots',
        icon: Users,
      },
      {
        path: '/hiring/calendar/*',
        component: HiringCalendar,
        name: 'Calendar',
        description: 'Plan out hiring timelines with hiring sprints, out-of-office planning and total hours required for hiring',
        icon: Users,
      },
      {
        path: '/hiring/rate-card',
        component: HiringRateCard,
        name: 'Rate Card',
        description: 'Fully loaded annual cost per department and grade — the planning cost of a position, priced monthly by envelopes',
        icon: Users,
        requiredCapability: 'hiring:planning',
      },
      {
        path: '/hiring/positions',
        component: HiringPositionLedger,
        name: 'Position Planning',
        description: 'Position planning: one row per budgeted head, tracked planned through filled and priced at the rate card',
        icon: Users,
        requiredCapability: 'hiring:planning',
      },
      {
        path: '/hiring/envelopes',
        component: HiringEnvelopes,
        name: 'Envelopes',
        description: 'Monthly headcount budget envelopes per department and program: target vs actuals vs committed hiring, dual-anchor status, known-departure credits, and derivation provenance',
        icon: Users,
        requiredCapability: 'hiring:planning',
      },
      {
        path: '/hiring/rollup',
        component: HiringRollup,
        name: 'Rollup',
        description: 'Leadership rollup for the monthly review: envelope status per department and program, junior-to-senior headcount ratios, slipped starts, and open exceptions with owners and review dates',
        icon: Users,
        requiredCapability: 'hiring:planning',
      },
      {
        path: '/hiring/health',
        component: HiringHealth,
        name: 'Health',
        description: 'Headcount system status: derivation freshness across the board, attribution snapshot, and envelopes, with attribution QA and where the numbers came from',
        icon: Users,
      },
    ],
  },
  sales: {
    id: 'sales',
    name: 'sales',
    displayName: 'Sales',
    icon: TrendingUp,
    defaultRoute: '/sales/workflow',
    description: 'Track sales forecasts, pipeline, deals, and closed won opportunities',
    routes: [
      {
        path: '/sales/workflow',
        component: SalesWorkflow,
        name: 'Workflow',
        description: 'Kanban deal board for managing active deals by employee. Supports team combination tracking, and a deadline-based calendar view',
        icon: Workflow,
      },
      {
        path: '/sales/audits',
        component: SalesAudits,
        name: 'Audits',
        description: 'Tracks auditors by division and manages RFP/audit records with DRI/PFT assignments, deal linking, and outcome recording',
        icon: TrendingUp,
      },
      {
        path: '/sales/pipeline',
        component: SalesPipeline,
        name: 'Pipeline',
        description: 'Executive view of sales funnel health showing companies created, initial meetings, and closed-won trends with lead source attribution and weighted pipeline',
        icon: TrendingUp,
      },
      {
        path: '/sales/deals',
        component: SalesDeals,
        name: 'Deals',
        description: 'Active pipeline tracking with initial meetings by fit score, lead-to-meeting and meeting-to-closed conversion rates, and a detailed active deals table',
        icon: TrendingUp,
      },
      {
        path: '/sales/closed',
        component: SalesClosed,
        name: 'Closed',
        description: 'Historical closed-won performance with quarterly cohort analysis, revenue vs fiscal year targets, and a deals table with time-to-close metrics',
        icon: TrendingUp,
      },
      {
        path: '/sales/firmographics',
        component: SalesFirmographics,
        name: 'Firmographics',
        description: 'ICP analysis across company size, industry, country, funding type, and business stage with conversion rates and fit score trends',
        icon: TrendingUp,
      },
      {
        path: '/sales/resourcing',
        component: SalesResourcing,
        name: 'Resourcing',
        description: 'Demand vs supply matchmaking comparing incoming weighted deals against available workflow capacity spots, with probability-weighted FTE and spend metrics',
        icon: TrendingUp,
      },
      {
        path: '/sales/pricing',
        component: SalesPricing,
        name: 'Pricing',
        description: 'Fee calculator for new deals. Input monthly ad spend, select flat or tiered model, view calculated fees, effective rate, discount score, and comparable benchmarks',
        icon: DollarSign,
      },
    ],
  },
  tools: {
    id: 'tools',
    name: 'tools',
    displayName: 'Tools',
    icon: Wrench,
    defaultRoute: '/tools/overview',
    description: 'Various tools and utilities',
    hidden: true,
    routes: [
      {
        path: '/tools/overview',
        component: ToolsOverview,
        name: 'Overview',
        icon: Wrench,
      },
      {
        path: '/tools/charts',
        component: ToolsCharts,
        name: 'Charts',
        icon: BarChart3,
      },
      {
        path: '/tools/compensation-calculator',
        component: ToolsCompensationCalculator,
        name: 'Compensation Calculator',
        icon: DollarSign,
      },
    ],
  },
  knowledge: {
    id: 'knowledge',
    name: 'knowledge',
    displayName: 'Knowledge',
    icon: BookOpen,
    defaultRoute: '/knowledge',
    description: 'Browse company knowledge base and documentation',
    hiddenFromSwitcher: true,
    routes: [
      {
        path: '/knowledge/*',
        component: KnowledgePage,
        name: 'Knowledge',
        icon: BookOpen,
      },
    ],
  },
  forecast: {
    id: 'forecast',
    name: 'forecast',
    displayName: 'Forecasting',
    icon: LineChart,
    defaultRoute: '/forecast',
    description: 'Finance forecasting pipeline — inputs, config, run controls, and results',
    hidden: true,
    hiddenFromSwitcher: true,
    routes: [
      {
        path: '/forecast/*',
        component: ForecastPage,
        name: 'Forecast',
        icon: LineChart,
      },
    ],
  },
  admin: {
    id: 'admin',
    name: 'admin',
    displayName: 'Administration',
    icon: Shield,
    defaultRoute: '/admin/users',
    description: 'System administration, user management, and global settings',
    hidden: true,
    routes: [
      {
        path: '/admin/users',
        component: AdminUsers,
        name: 'User Management',
        icon: Users,
      },
      {
        path: '/admin/data-import',
        component: AdminDataImport,
        name: 'Data Import',
        icon: Database,
      },
      {
        path: '/admin/cycles',
        component: AdminCycles,
        name: 'Cycle Management',
        icon: RefreshCw,
      },
      {
        path: '/admin/spend-config',
        component: AdminSpendConfig,
        name: 'Spend Configuration',
        icon: DollarSign,
      },
      {
        path: '/admin/slack-integration',
        component: AdminSlackIntegration,
        name: 'Slack Integration',
        icon: MessageSquare,
      },
      {
        path: '/admin/slack-templates',
        component: AdminSlackTemplates,
        name: 'Slack Templates',
        icon: MessageSquare,
      },
      {
        path: '/admin/asana-integration',
        component: AdminAsanaIntegration,
        name: 'Asana Integration',
        icon: Trello,
      },
      {
        path: '/admin/apollo-enrichment',
        component: AdminApolloEnrichment,
        name: 'Apollo Enrichment',
        icon: Database,
      },
      {
        path: '/admin/functions/*',
        component: AdminFunctionHealth,
        name: 'Function Health',
        icon: Activity,
      },
    ],
  },
};

// Helper functions
export function getDomainById(domainId: string): DomainConfig | undefined {
  return DOMAINS[domainId];
}

export function getAvailableDomains(): DomainConfig[] {
  return Object.values(DOMAINS).filter(domain =>
    !domain.hidden && hasAccessToDomain(domain)
  );
}

export function getAccessibleDomains(): DomainConfig[] {
  return Object.values(DOMAINS).filter(domain =>
    !domain.hiddenFromSwitcher && hasAccessToDomain(domain)
  );
}

function hasAccessToDomain(domain: DomainConfig): boolean {
  const { groupAccess } = useAuthStore.getState();
  if (!groupAccess) return false;
  return domain.routes.some(r => hasGroupRouteAccess(r.path, groupAccess));
}

export function hasAccessToRoute(route: DomainRoute): boolean {
  const { groupAccess } = useAuthStore.getState();
  if (!groupAccess) return false;
  if (!hasGroupRouteAccess(route.path, groupAccess)) return false;
  if (route.requiredCapability && !hasCapability(route.requiredCapability, groupAccess)) {
    return false;
  }
  return true;
}

function getDomainRoutes(domainId: string): DomainRoute[] {
  const domain = getDomainById(domainId);
  if (!domain) return [];
  return domain.routes.filter(route => hasAccessToRoute(route));
}

// Returns all route definitions across all domains with no filtering.
// Used for route matching and access-check logic in the router.
export function getAllRouteDefinitions(): DomainRoute[] {
  return Object.values(DOMAINS).flatMap(domain => domain.routes);
}

// Returns routes the user is permitted to access.
// Iterates all DOMAINS (including hidden) — hidden is purely a switcher UI concern.
export function getAllRoutes(): DomainRoute[] {
  return Object.values(DOMAINS).flatMap(domain =>
    getDomainRoutes(domain.id)
  );
}

// ── Route Tree (for admin UI group editor) ──────────────────────────────────

interface RouteTreeEntry {
  path: string;
  name: string;
}

interface RouteTreeDomain {
  id: string;
  displayName: string;
  routes: RouteTreeEntry[];
}

/**
 * Returns all domains and their routes as a flat tree structure.
 * Used by the admin group editor to render a checklist of assignable routes.
 */
export function getRouteTree(): RouteTreeDomain[] {
  return Object.values(DOMAINS).map(domain => ({
    id: domain.id,
    displayName: domain.displayName,
    routes: domain.routes.map(route => ({
      path: route.path,
      name: route.name,
    })),
  }));
}

